import React from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { slugMap } from '../../lib/slugs';
import { getPageMeta, SITE_NAME } from '../../lib/pageMeta';

/**
 * Every head tag for the current page, written from one place.
 *
 * Rendered once inside the router — NOT per page. The 106 page components stay
 * untouched: this works out which page is showing from the URL, then looks the
 * metadata up by the same key slugMap uses. A new page gets its metadata by
 * adding one entry to pageMeta.js, not by editing a component.
 *
 * What it emits, and why each matters here:
 *
 *   <html lang>   Was hardcoded "en" in index.html, so all 102 Spanish pages
 *                 told crawlers they were English.
 *   <title>       Was the same "HANSA-FLEX Spain" on all 205 URLs, which makes
 *                 every page look like a duplicate of every other.
 *   description   None existed, so Google invented snippets from page text.
 *   canonical     None existed. Self-referencing canonicals stop query strings
 *                 (?utm_source=…) being indexed as separate pages.
 *   hreflang      Mirrors the sitemap, linking the EN and ES versions so they
 *                 stop competing with each other.
 *   Open Graph    Links shared on LinkedIn or WhatsApp previewed as
 *                 "HANSA-FLEX Spain" with no description, for every page.
 *
 * NOTE: these tags are written by JavaScript, so a crawler only sees them if it
 * executes JS. Google does; Bing and social scrapers largely do not. Getting
 * them into the raw HTML needs the prerender step — this component is what
 * that step would capture.
 */

const ORIGIN = 'https://www.hansa-flex.es';
const LANGS = ['en', 'es'];

/** '/' is stored for the home page, which must not produce a doubled slash. */
const urlFor = (lang, slug) => `${ORIGIN}/${lang}${slug === '/' ? '/' : slug}`;

/**
 * Which slugMap entry the current path belongs to.
 *
 * Longest match wins: some slugs are nested ('/casos-de-exito/moldtech-…'),
 * and a shorter slug that happens to be a prefix would otherwise claim them.
 */
function matchPage(pathname) {
  const stripped = pathname.replace(/^\/(en|es)(?=\/|$)/i, '') || '/';
  const clean = stripped.length > 1 ? stripped.replace(/\/$/, '') : stripped;

  let best = null;
  Object.entries(slugMap).forEach(([key, langs]) => {
    LANGS.forEach((lang) => {
      const slug = langs[lang];
      if (!slug) return;
      if (slug === clean && (!best || slug.length > best.slug.length)) {
        best = { key, slug };
      }
    });
  });
  return best ? best.key : null;
}

function PageMeta() {
  const location = useLocation();
  const pathname = location.pathname || '/';
  const lang = /^\/es(\/|$)/i.test(pathname) ? 'es' : 'en';

  const key = matchPage(pathname);
  const { title, description, keywords, image } = getPageMeta(key, lang);

  // Share images must be absolute: LinkedIn, WhatsApp and Facebook fetch them
  // from their own servers, where a path like /og-image.jpg means nothing.
  const imageUrl = /^https?:\/\//.test(image) ? image : `${ORIGIN}${image}`;

  // An unknown path is the 404 page. It gets no canonical and no hreflang —
  // pointing them at a page that does not exist would be worse than omitting
  // them — and it is told not to index, so dead URLs do not accumulate in
  // search results.
  const isKnownPage = Boolean(key);

  const entry = isKnownPage ? slugMap[key] : null;
  const canonical = isKnownPage ? urlFor(lang, entry[lang]) : null;
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {/* Included because the SEO brief asks for it. Worth knowing: Google has
          ignored meta keywords since 2009 and Bing treats stuffing as a
          negative signal, so this earns nothing on its own — it does no harm
          either. Only emitted for pages that actually have their own list. */}
      {keywords && <meta name="keywords" content={keywords} />}

      {isKnownPage && <link rel="canonical" href={canonical} />}
      {isKnownPage &&
        LANGS.filter((l) => entry[l]).map((l) => (
          <link key={l} rel="alternate" hrefLang={l} href={urlFor(l, entry[l])} />
        ))}
      {/* Spanish is x-default: a .es domain serving the Spanish market. Must
          match X_DEFAULT_LANG in scripts/generate-sitemap.js and the fallback
          in App.js, or Google discards the whole hreflang set. */}
      {isKnownPage && entry.es && (
        <link rel="alternate" hrefLang="x-default" href={urlFor('es', entry.es)} />
      )}
      {!isKnownPage && <meta name="robots" content="noindex, follow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {isKnownPage && <meta property="og:url" content={canonical} />}
      <meta property="og:locale" content={lang === 'es' ? 'es_ES' : 'en_GB'} />
      <meta property="og:image" content={imageUrl} />
      {/* Dimensions let a platform reserve the right space before the image
          has downloaded, which is what stops a card rendering text-only on a
          slow connection. */}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={SITE_NAME} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={imageUrl} />
    </Helmet>
  );
}

export default PageMeta;
